import type { ChangeEvent } from "react"
import React, { useEffect } from "react"

import ReactGA from "react-ga4"
import { useTranslation } from "react-i18next"
import { useMutation, useQuery } from "react-query"
import { useSelector } from "react-redux"

import * as Yup from "yup"
import { DialogContent, Grid } from "@mui/material"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, Checkbox, InputChips, NotificationUtils, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormik } from "formik"
import { t } from "i18next"
import moment from "moment"

import { SelectWithSearch } from "../../components/UI/SelectWithSearch"
import { SideBar } from "../../components/UI/SideBar"
import { KonanAvatarComponent } from "../../components/core/KonanAvatar"
import { SidAPI } from "../../services/SidAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import type { EditUserRequest } from "../../types/custom/Permissions"
import type { GroupRetrieve } from "../../types/generated/authentication/GroupRetrieve"
import type { OrganizationInvitationBulkRequest } from "../../types/generated/authentication/OrganizationInvitationBulkRequest"
import type { OrganizationInvitationBulkResponse } from "../../types/generated/authentication/OrganizationInvitationBulkResponse"
import type { OrganizationUser } from "../../types/generated/authentication/OrganizationUser"
import type { Role } from "../../types/generated/authentication/Role"
import { getRoleDisplayName } from "../../utils/rolesHelpers"
import type { MemberInviteDialogProps } from "./Interfaces"

import styles from "./Members.module.scss"

//Adapted from https://stackoverflow.com/questions/61891269/yup-validation-how-to-get-the-value
const validationSchema = Yup.object({
  email: Yup.array()
    .required(t("MemberInviteDialog.validation.mustInviteAtLeastOne", { ns: "settings" }))
    .of(
      Yup.string().email(
        (el) => el.value + " " + t("MemberInviteDialog.validation.isInvalidEmail", { ns: "settings" }),
      ),
    )
    .test({
      test: (arr) => (arr as string[]).length > 0,
    }),
  role: Yup.string().required(t("MemberInviteDialog.validation.mustChooseRole", { ns: "settings" })),
  groups: Yup.array()
    .of(Yup.string().required())
    .required(t("MemberInviteDialog.validation.mustChooseAtLeastOneGroup", { ns: "settings" }))
    .test({
      test: (arr) => (arr ? arr?.length > 0 : false),
    }),
})

/**
 * Component Dialog for inviting new users(s), only admin can invite users.
 * @param {boolean} isOpen indicates dialog open or not
 * @param {void} onClose callback fn to handle closing dialog
 * @param {OrganizationUser} member callback fn to handle closing dialog
 * @returns {React.ReactElement}
 */
export function MemberInviteDialog(props: Readonly<MemberInviteDialogProps>): React.ReactElement {
  const { isOpen, onClose, member } = props

  const OrganizationUID = useSelector((state: RootState) => state.auth.organizationId) as string
  const isOwner = useSelector((state: RootState) => state.auth.roleIsOwner)

  const { t } = useTranslation("settings")

  // Fetching organization roles
  const { isLoading: isRolesLoading, data: roles } = useQuery<AxiosResponse<Role[]>, AxiosError>(
    ["roles", OrganizationUID],
    () => SidAPI.fetchRoles(OrganizationUID),
    {
      refetchOnMount: false,
      enabled: !!OrganizationUID,
    },
  )

  // Fetching organization groups
  const { isLoading: isGroupsLoading, data: groups } = useQuery<AxiosResponse<GroupRetrieve[]>, AxiosError>(
    ["groups", OrganizationUID],
    () => SidAPI.fetchGroups(OrganizationUID),
    {
      enabled: !!OrganizationUID,
      refetchOnMount: false,
    },
  )

  // sending user invitations
  const inviteUserMutation = useMutation<
    AxiosResponse<OrganizationInvitationBulkResponse>,
    AxiosError,
    {
      orgId: string
    } & OrganizationInvitationBulkRequest
  >(SidAPI.inviteUsers, {
    onSuccess: async (response) => {
      response.data.fail.map((item) =>
        NotificationUtils.toast(
          item?.detail?.sender ??
            item?.detail?.email?.[0]?.replace(
              "User",
              t("membersDialog.notifications.inviteUser.specificMutationError", { email: item.email }),
            ) ??
            t("membersDialog.notifications.inviteUser.individualMutationError", { email: item.email }),
          {
            severity: "error",
          },
        ),
      )
      await queryClient.invalidateQueries("Organization")
      await queryClient.invalidateQueries("OrganizationInvitation")
    },
  })

  // edit user
  const editUserMutation = useMutation<AxiosResponse, AxiosError, EditUserRequest>(SidAPI.editUser, {
    onSuccess: async () => {
      NotificationUtils.toast(t("membersDialog.notifications.editUser.success"), {
        severity: "success",
      })

      await queryClient.invalidateQueries("Organization")
      await queryClient.invalidateQueries("OrganizationInvitation")

      await queryClient.invalidateQueries(["roles", OrganizationUID])
      await queryClient.invalidateQueries(["groups", OrganizationUID])

      await queryClient.invalidateQueries(["change-history", OrganizationUID])
    },
  })

  const closeDialog = (): void => {
    // Call parent handler
    onClose()

    // To prevent flickering of Snackbars when closing the dialog
    setTimeout(() => {
      inviteUserMutation.reset()
      formik.resetForm()
    }, 300)
  }

  // init formik
  const formik = useFormik({
    initialValues: {
      email: member?.email ? [member?.email] : [],
      role: member?.role.uid ?? "",
      groups: member?.groups.map((group) => group.uid) ?? [],
      lastInput: "",
    },
    validateOnMount: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (member) {
        await editUserMutation.mutateAsync({
          userId: member.uid,
          role: values.role,
          groups: values.groups,
        })
      } else {
        // invite new users
        try {
          //if the user forgot to press enter and the last email entered is valid
          const res = await inviteUserMutation.mutateAsync({
            orgId: OrganizationUID,
            role: values.role,
            groups: values.groups,
            emails:
              //if the user forgot to press enter and the last email entered is valid
              !!values.lastInput && Yup.string().email().isValidSync(values.lastInput)
                ? [...values.email, values.lastInput]
                : values.email,
            expiry_date: moment().add(7, "days").toISOString(),
          })

          ReactGA.event({
            category: "Users",
            action: "Invited users to organization",
          })

          if (res.data.fail.length === 0) {
            NotificationUtils.toast(t("membersDialog.notifications.inviteUser.success"), { severity: "success" })
            resetForm({})
            closeDialog()
          }
          // TODO:: figure out error type
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          if (e.response?.data?.detail) {
            NotificationUtils.toast(e.response.data.detail, { severity: "error" })
          } else {
            NotificationUtils.toast(t("membersDialog.notifications.inviteUser.error"), {
              severity: "error",
            })
          }
        }
      }

      resetForm({})
      closeDialog()
    },
  })

  useEffect(() => {
    const resources: string[] = []

    if (member) {
      groups?.data.forEach((group) => {
        group.users.some((user: OrganizationUser) => user.email === member.email) && resources.push(group.uid)
      })

      formik.setFieldValue("groups", resources)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups?.data, member, member?.email])

  return (
    <SideBar
      isOpen={isOpen}
      onClose={() => {
        formik.resetForm()
        closeDialog()
      }}
      title={member ? t("membersDialog.dialogTitle.edit") : t("membersDialog.dialogTitle.invite")}
      closeIcon={{
        icon: "close",
        position: "right",
      }}
      variant="floating"
      paperStyle={{
        width: "40%",
      }}
      actions={[
        <Button onClick={closeDialog} variant="secondary" disabled={formik.isSubmitting}>
          {t("membersDialog.buttons.cancel")}
        </Button>,
        <Button
          onClick={formik.submitForm}
          id="send-invite"
          variant="primary"
          disabled={!formik.isValid}
          isLoading={formik.isSubmitting}
        >
          {member ? t("membersDialog.buttons.edit") : t("membersDialog.buttons.invite")}
        </Button>,
      ]}
    >
      <DialogContent className={styles.membersInvitationDialog}>
        <Grid container gap={2}>
          {/* Showing error snackbar */}
          {inviteUserMutation.data !== undefined && inviteUserMutation.data?.data?.fail?.length > 0 && (
            <Grid item xs={12} className={styles.newUserContainer}>
              {inviteUserMutation.data.data.fail.map((item) => (
                <Snackbar
                  severity="error"
                  fullWidth
                  description={
                    item?.detail?.sender ||
                    item?.detail?.email ||
                    t("membersDialog.notifications.inviteUser.individualError")
                  }
                />
              ))}
            </Grid>
          )}

          <Grid item xs={12}>
            {member ? (
              <KonanAvatarComponent createdBy={member.email} title={member.email} createdAt={member.created_at} />
            ) : (
              <InputChips
                value={formik.values.lastInput}
                handleChange={(e: InputChangeEvent) => {
                  formik.setFieldValue("lastInput", e.target.value)
                  formik.validateForm()
                }}
                handleBlur={() => {
                  if (!!formik.values.lastInput && Yup.string().email().isValidSync(formik.values.lastInput)) {
                    formik.setFieldValue("email", [...formik.values.email, formik.values.lastInput])
                    formik.setFieldValue("lastInput", "")
                  }
                  formik.validateForm()
                }}
                setEnteredChipsValue={(emails) => {
                  formik.setFieldValue("email", emails)
                  formik.validateForm()
                }}
                enteredChipsValue={formik.values.email}
                email
                fullWidth
                label={t("membersDialog.emailsInput.label")}
                placeholder={"John@doe.com"}
                description={(formik.errors.email as string) ?? t("membersDialog.emailsInput.description")}
              />
            )}
          </Grid>

          <Grid container style={{ maxHeight: "400px" }}>
            <Grid item xs={12}>
              <Grid item xs={12}>
                <Typography variant="h3-bold" gutterBottom>
                  {t("membersDialog.roleInput.title")}
                </Typography>
              </Grid>

              <Grid container item xs={12}>
                <SelectWithSearch
                  options={[
                    { label: t("membersDialog.roleInput.placeholder"), value: "", isDisabled: true },
                    ...(roles?.data
                      ? [...roles.data]
                          // if isOwner then return all roles, if not isOwner then return roles that aren't owner role
                          .filter((role) => !role.is_owner || isOwner)
                          .sort((a, b) => (a.name === "USER" ? -1 : b.name === "USER" ? 1 : 0))
                          .map((role) => {
                            return {
                              label: getRoleDisplayName(role?.name),
                              value: role?.uid,
                            }
                          })
                      : []),
                  ]}
                  initialValue={
                    member?.role_label
                      ? getRoleDisplayName(member.role_label)
                      : t("membersDialog.roleInput.placeholder")
                  }
                  placeHolder={t("membersDialog.roleInput.placeholder")}
                  searchInputPlaceHolder={t("membersDialog.roleInput.searchPlaceholder")}
                  isOptionsLoading={isRolesLoading}
                  fullWidth
                  onSelectMenuItem={(item: { label: string; value: string }) => {
                    formik.setFieldValue("role", item.value)
                  }}
                  hideDescription
                />
              </Grid>
            </Grid>

            <Grid item xs={12} mt={1.5}>
              <Grid item xs={12}>
                <Typography variant="h3-bold" gutterBottom>
                  {t("membersDialog.groupInput.title")}
                </Typography>
              </Grid>

              <Grid
                container
                item
                xs={12}
                spacing={1}
                className="scrollbar"
                style={{ flex: "1 1 auto", overflowY: "auto", maxHeight: "350px" }}
              >
                {/* Add loading state */}
                {!isGroupsLoading &&
                  [...(groups?.data ?? [])]
                    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                    .map((group) => (
                      <Grid item xs={12} key={group.uid}>
                        <Checkbox
                          label={group.name}
                          name={group.name}
                          value={group.uid}
                          checked={formik.values.groups.includes(group.uid)}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            // since the onChange can toggle between checked and unChecked and the method is custom
                            // not just setting a normal field in formik so we had to make that check
                            if (e.target?.checked) {
                              formik.setFieldValue("groups", [...formik.values.groups, group.uid])
                            } else {
                              formik.setFieldValue(
                                "groups",
                                formik.values.groups.filter((item: string) => item !== group.uid),
                              )
                            }
                          }}
                        />
                      </Grid>
                    ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </SideBar>
  )
}
